/**
 * Letter Throwing Animation Components
 *
 * v8 Letter Throwing animation system for DramaDetector.
 * Provides a modern, immersive analysis experience alongside
 * the classic 90s web interface.
 *
 * Platform-specific implementations:
 * - Native: Reanimated 3 (UI thread animations)
 * - Web: CSS + RAF (to be implemented in Phase 2)
 *
 * @example
 * ```tsx
 * import { LetterThrowingAnimation } from '@/components/letter-throwing';
 *
 * <LetterThrowingAnimation
 *   text={dramaText}
 *   speed="3"
 *   onComplete={() => setPhase('results')}
 *   onSkip={() => setPhase('results')}
 * />
 * ```
 */

// Main component (platform-resolved by Metro)
// Metro will resolve to .native.tsx for iOS/Android, .web.tsx for web
export { default as LetterThrowingAnimation } from './LetterThrowingAnimation';

// Sub-components (platform-resolved)
export { default as FlyingLetter } from './FlyingLetter';
export { default as AccumulatedText } from './AccumulatedText';

// Hooks
export { useSpeedPresets, SPEED_PRESETS, getWordsForLine } from './hooks/useSpeedPresets';
export { useLetterScheduler } from './hooks/useLetterScheduler';
export {
  useReducedMotionFallback,
  useAnimationWithFallback,
} from './hooks/useReducedMotionFallback';

// Types
export type {
  SpeedPreset,
  SpeedSettings,
  AnimationPhase,
  AnimationState,
  AnimationVariant,
  ConvergencePhase,
  LetterPosition,
  WordConvergenceState,
  LetterThrowingAnimationProps,
  FlyingLetterProps,
  AccumulatedTextProps,
} from './types';

export {
  CHROMATIC_COLORS,
  PHASE_TIMING,
  LETTER_ZOOM_PHASE_TIMING,
  LETTER_ZOOM_STEPS,
  generateDynamicZoomSteps,
  calculateDynamicStepBoundaries,
} from './types';

export type { ZoomStepTextLength, ZoomStep, DynamicZoomStep } from './types';
